import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Building, CaretDown, X, Check, Warning, MagnifyingGlass } from "@phosphor-icons/react";
import { listClients } from "../lib/api";
import { BlockCard } from "./BlockCard";
import { ClientDeadlineStrip } from "./ClientDeadlineStrip";
import { labelFor } from "@/lib/enumLabels";

/**
 * v0.4.38 B1 — Filing model chip rendered below each client name
 * in the selector dropdown. Reads `filing_model` (CP1 stored
 * field) with a fall-through to `derived_filing_model` for
 * profiles created before CP1.
 *
 * Visual rule:
 *   UNION_OSS   + msi  - "UNION OSS, DE" (teal)
 *   UNION_OSS   - msi  - "UNION OSS, no MSI" (amber)
 *   LOCAL_RETURNS      - "LOCAL RETURNS" (slate)
 *   HYBRID + msi       - "HYBRID, DE" (teal)
 *   HYBRID - msi       - "HYBRID, no MSI" (amber)
 *   null / unknown     - "filing model not set" (italic slate)
 */
const FilingModelChip = ({ client }) => {
  const { t } = useTranslation();
  const model = (client?.filing_model || "").toUpperCase();
  const msi = (client?.oss_msi_country || "").toUpperCase();
  const testid = `client-selector-filing-model-chip-${client?.code || "?"}`;
  if (!model) {
    return (
      <span data-testid={testid}
            className="mt-1 inline-block text-[11px] tracking-normal italic text-slate-500 dark:text-slate-400">
        — {t("ui.client_selector.filing_model_not_set", { defaultValue: "filing model not set" })}
      </span>
    );
  }
  if (model === "LOCAL_RETURNS") {
    return (
      <span data-testid={testid}
            className="mt-1 inline-flex items-center px-2 py-0.5 text-[10px] tracking-normal text-slate-500 dark:text-slate-400 bg-slate-700/40 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded">
        LOCAL RETURNS{/* i18n-ignore: filing-model code */}
      </span>
    );
  }
  const label = model === "UNION_OSS" ? "UNION OSS" : "HYBRID";
  if (!msi) {
    return (
      <span data-testid={testid}
            className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] tracking-normal text-amber-700 dark:text-amber-300 bg-amber-500/10 dark:bg-amber-900/40 border border-amber-200 dark:border-amber-800 rounded">
        <Warning size={10} weight="fill" />
        {label} · {t("ui.client_selector.no_msi", { defaultValue: "no MSI" })}
      </span>
    );
  }
  return (
    <span data-testid={testid}
          className="mt-1 inline-flex items-center px-2 py-0.5 text-[10px] tracking-normal text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-900/40 border border-teal-300 dark:border-teal-700 rounded">
      {label} · {msi}{/* i18n-ignore: filing-model and country codes */}
    </span>
  );
};

/**
 * v0.4.37 R3 — Client selector card.
 *
 * Replaces the old native dropdown with a card-first design:
 *   - When closed: large client name (20px bold white) + small
 *     grey code chip + meta line + "Change client →" button.
 *     Most visually prominent element on the page above the
 *     filing period card.
 *   - When open: slide-down panel listing every client as a
 *     button card. Keyboard-accessible: Enter/Space opens; arrow
 *     keys move focus; Enter on a row selects; Escape closes.
 *
 * Persists last selection to `localStorage` so an operator's
 * client survives reloads. Disabled / mid-calc is opaque (calc in
 * progress blocks switching).
 */
export const ClientSelector = ({ value, onChange, disabled, isAdmin, step = null, accent = null, children = null }) => {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [changing, setChanging] = useState(false);
  const [focusIdx, setFocusIdx] = useState(-1);
  // v0.5.0i+11 — search box. Operator brief: scrolling through
  // 258 clients to pick one is a nightmare. Filter by name OR
  // code (case-insensitive substring) so "ACM" finds both
  // "Acme Co" and the S00123 ACM-coded suite.
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    listClients()
      .then((rows) => { if (!cancelled) setClients(rows); })
      .catch((e) => { if (!cancelled) setError(e?.message || String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!value && clients.length > 0) {
      const saved = localStorage.getItem("vat.selectedClientId");
      const candidate = clients.find((c) => c.id === saved)
        || clients.find((c) => c.is_default)
        || clients[0];
      if (candidate) onChange?.(candidate.id);
    }
  }, [clients, value, onChange]);

  const selected = clients.find((c) => c.id === value);

  // v0.5.0i+11 — filter clients by case-insensitive substring on
  // name + code. Pure filter — no "force-include selected" hack
  // because that produces a misleading match count (e.g. typing
  // "S0010" can't possibly match "Test Co"). The selected client
  // stays selected even when filtered out of view; clear the
  // search to see it again.
  const filteredClients = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => (
      (c.name || "").toLowerCase().includes(q)
      || (c.code || "").toLowerCase().includes(q)
    ));
  }, [clients, query]);

  const select = (id) => {
    localStorage.setItem("vat.selectedClientId", id);
    onChange?.(id);
    setChanging(false);
    setFocusIdx(-1);
    setQuery("");
  };

  // Keyboard navigation inside the slide-down panel (R3 amendment).
  // v0.5.0i+11 — nav iterates over `filteredClients` so arrow keys
  // respect the search filter (operator can search → arrow-down →
  // Enter without seeing hidden rows pop up under the focus ring).
  const onPanelKey = (e) => {
    if (!changing) return;
    if (e.key === "Escape") { setChanging(false); setQuery(""); return; }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusIdx((i) => Math.min(filteredClients.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" && focusIdx >= 0) {
      e.preventDefault();
      const c = filteredClients[focusIdx];
      if (c) select(c.id);
    }
  };

  useEffect(() => {
    if (changing && searchRef.current) {
      // Focus the search box first when the panel opens — operator
      // can start typing immediately without an extra click.
      searchRef.current.focus();
    }
  }, [changing]);

  // Reset focus when the filtered list changes (e.g. operator
  // typed another character) so the focus ring doesn't point at
  // a row that just got filtered out.
  useEffect(() => {
    setFocusIdx(-1);
  }, [query]);

  if (loading) {
    return (
      <div className="text-[13px] text-slate-600 dark:text-slate-300 px-5 py-3">
        {t("ui.client_selector.loading", "Loading clients…")}{children}
      </div>
    );
  }
  if (error || clients.length === 0) {
    return (
      <div className="text-[13px] text-red-700 dark:text-red-300 px-5 py-3">
        {error || t("ui.client_selector.empty", "No clients configured.")}{children}
      </div>
    );
  }

  return (
    <BlockCard
      testid="client-selector-card" step={step} accent={accent}
      icon={Building}
      title={t("ui.client_selector.block_title", "Who you are filing for")}
      purpose={t("ui.client_selector.block_purpose",
                 "The entity, its registrations and what is due next")}
    >
      <div className="flex items-center gap-4">
        <div className="flex-1 min-w-0" data-testid="client-selector-display">
          <div
            data-testid="client-selector-display-name"
            // v0.6.6 — was `text-white` unconditionally against a
            // `bg-white dark:bg-slate-900` container: the active client
            // name (the single most important "which client am I filing
            // for" indicator) was invisible in light theme.
            className="font-bold text-slate-900 dark:text-white truncate"
            style={{ fontSize: "20px", lineHeight: "1.2" }}
          >{selected?.name}</div>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span
              data-testid="client-selector-display-code"
              className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-[10px] tabular-nums font-semibold"
            >{selected?.code}</span>
            {selected?.is_default && (
              <span className="text-[10px] tracking-normal text-amber-700/80 font-bold">
                {t("ui.client_selector.default_label")}
              </span>
            )}
            {/* v0.6.12 — language, so Inter. Only the COUNT keeps the monospace utility,
                which is what the stylesheet has always said the class is
                for: "numbers (money, percentages, IDs, hashes)". */}
            <span className="text-[13px] text-slate-600 dark:text-slate-300">
              <span className="tabular-nums">{selected?.company_vat_numbers?.length || 0}</span>{" "}
              {t("ui.client_selector.regs_suffix", "VAT registrations")}
              {/* v0.6.12 — these two printed the RAW enum: the operator was
                  reading "NON_EU" next to their client's name. And the
                  separators were hardcoded, so an unset filing model (the
                  common case — FilingModelChip has a whole branch for it)
                  left a trailing " · " hanging off the end of the line,
                  which is what made it look truncated. Both fixed by
                  joining only the parts that exist. */}
              {[labelFor("seller_establishment", selected?.seller_establishment),
                selected?.filing_model
                  ? labelFor("filing_model", selected.filing_model)
                  : null,
              ].filter(Boolean).map((part) => ` · ${part}`).join("")}
            </span>
          </div>
          {/* v0.6.12 — upcoming deadlines for this client, on the screen where
              the operator picks which period to run. Read-only, and absent
              rather than broken if the fetch fails. */}
          <ClientDeadlineStrip clientId={selected?.id} />
        </div>
        <button
          type="button"
          data-testid="client-selector-change-btn"
          disabled={disabled}
          onClick={() => setChanging((s) => !s)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setChanging((s) => !s);
            }
          }}
          aria-expanded={changing}
          aria-controls="client-selector-panel"
          className={`inline-flex items-center gap-1.5 font-semibold text-xs px-4 py-2.5 rounded-xl border ${
            disabled
              ? "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 cursor-not-allowed"
              : "border-teal-300 dark:border-teal-700 text-teal-700 dark:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900/40 dark:bg-teal-900/40"
          }`}
        >
          {changing ? <><X size={12} weight="bold" />{t("ui.client_selector.close_btn")}</> : <>{t("ui.client_selector.change_btn")}<CaretDown size={12} weight="bold" /></>}
        </button>
      </div>

      {changing && (
        <div
          id="client-selector-panel"
          ref={panelRef}
          tabIndex={-1}
          role="listbox"
          aria-label={t("ui.client_selector.label", "Filing this period for")}
          onKeyDown={onPanelKey}
          data-testid="client-selector-panel"
          className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 space-y-2 outline-none"
        >
          {/* v0.5.0i+11 — search box. Scrolling 258 clients is a
              nightmare; typing 2–3 chars filters instantly. The
              live count below sets expectations + tells the
              operator when their search matched zero. */}
          <div className="relative">
            <MagnifyingGlass
              size={14}
              weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 pointer-events-none"
            />
            <input
              ref={searchRef}
              type="text"
              data-testid="client-selector-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t(
                "ui.client_selector.search_placeholder",
                "Search entities by name or code…"
              )}
              aria-label={t(
                "ui.client_selector.search_placeholder",
                "Search entities by name or code…"
              )}
              className="w-full pl-9 pr-9 py-2.5 text-sm rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-50 placeholder-slate-400 dark:placeholder-slate-500 focus:border-teal-500 dark:focus:border-teal-400 focus:ring-2 focus:ring-teal-500/30 outline-none"
            />
            {query && (
              <button
                type="button"
                data-testid="client-selector-search-clear"
                onClick={() => { setQuery(""); searchRef.current?.focus(); }}
                aria-label={t("ui.client_selector.clear_search", "Clear search")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <X size={12} weight="bold" />
              </button>
            )}
          </div>
          <div
            data-testid="client-selector-result-count"
            className="text-[11px] text-slate-500 dark:text-slate-400 px-1"
          >
            {query
              ? t(
                  "ui.client_selector.results_count",
                  "{{shown}} of {{total}} match",
                  { shown: filteredClients.length, total: clients.length }
                )
              : t(
                  "ui.client_selector.total_count",
                  "{{total}} entities",
                  { total: clients.length }
                )}
          </div>
          {filteredClients.length === 0 && (
            <div
              data-testid="client-selector-no-results"
              className="text-xs italic text-slate-500 dark:text-slate-400 px-3 py-4 text-center"
            >
              {t(
                "ui.client_selector.no_results",
                "No entities match \"{{query}}\". Try a different name or code.",
                { query }
              )}
            </div>
          )}
          {filteredClients.map((c, idx) => {
            const active = c.id === value;
            const focused = idx === focusIdx;
            return (
              <button
                key={c.id}
                type="button"
                role="option"
                aria-selected={active}
                data-testid={`client-selector-option-${c.code}`}
                onClick={() => select(c.id)}
                onFocus={() => setFocusIdx(idx)}
                tabIndex={0}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-colors ${
                  active
                    ? "bg-teal-50 dark:bg-teal-900/40 border-teal-500/50 dark:border-teal-700 text-teal-100 dark:text-teal-100"
                    : focused
                      ? "bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-50"
                      // v0.6.12 — this held BOTH dark:bg-slate-950 and
                      // dark:bg-slate-800, and dark:border-slate-700 twice.
                      // Whichever Tailwind emitted last silently won, so the
                      // resting state was never the colour either of them
                      // intended. One background, one border.
                      : "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:border-slate-300"
                }`}
              >
                <Building size={16} weight={active ? "fill" : "regular"} className={active ? "text-teal-700 dark:text-teal-300" : "text-slate-500 dark:text-slate-400"} />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{c.name}</div>
                  <div className="text-[11px] tabular-nums text-slate-600 dark:text-slate-300 truncate">
                    {c.code} · {(c.company_vat_numbers || []).length}{" "}
                    {t("ui.client_selector.regs_suffix", "VAT registrations")}
                  </div>
                  <FilingModelChip client={c} />
                </div>
                {active && <Check size={16} weight="bold" className="text-teal-700 dark:text-teal-300" />}
              </button>
            );
          })}
          {isAdmin && (
            // v0.6.8 fix — was a plain <a href>, which triggers a full
            // browser page reload instead of client-side navigation.
            // That reload destroys every in-memory React state,
            // including the active calc/review results held in
            // WorkflowContext — clicking this from mid-workflow made
            // the operator's whole session appear to "disappear" on
            // return. <Link> keeps the SPA session intact.
            <Link
              to="/admin/clients"
              data-testid="client-selector-manage-link"
              className="block text-center text-xs text-teal-700 dark:text-teal-300 hover:text-teal-700 dark:hover:text-teal-300 font-bold py-2"
            >{t("ui.client_selector.manage", "Manage clients")} →</Link>
          )}
        </div>
      )}
      {children && <div className="-mx-4 -mb-4 mt-4">{children /* A2-467: registrations sit with the client */}</div>}
    </BlockCard>
  );
};
